import {
  Flex,
  List,
  Paper,
  Title,
  Text,
  Button,
  Avatar,
  Image,
} from "@mantine/core";
import { FormattedMessage } from "react-intl";
import { usePOAStepsStore } from "../../../hooks/ui/poaStepsDataStore";
import {
  ArrowUpOnSquareStackIcon,
  ChartBarIcon,
  ChartPieIcon,
} from "@heroicons/react/24/solid";
import FullmaktskollenImage from "/fullmaktskollen-logo.png";
import { FooterButtons } from "../../footerButtons";
import { APP_POA_STEP, trackEvent } from "../../../utils/statsig";

export default function POAInfo({ onBack }: { onBack?: () => void } = {}) {
  const { nextStep, prevStep, app } = usePOAStepsStore();

  return (
    <Flex justify="center" align="center" data-testid="poa-info">
      <Flex direction="column" maw={672} w={"100%"}>
        <Paper px={app ? 0 : 30} radius={"24px"} my={app ? 0 : "2rem"}>
          <Title
            order={2}
            mb="2rem"
            ff={"var(--wallet-font-family-sambla-sans)"}
          >
            {app ? (
              <FormattedMessage
                id="app.poaOnb.readMore.headline"
                defaultMessage="Informationsfullmakt från fullmaktskollen"
              />
            ) : (
              "Vi behöver en fullmakt för att hämta information om din pension."
            )}
          </Title>
          <Text mb={"lg"}>
            Fullmakten hanteras av Fullmaktskollen, en tjänst som ägs gemensamt
            av Svensk Försäkring och Svenska försäkringsförmedlares förening.
            {!app &&
              " Information som vi hämtar in med stöd av din fullmakt kommer att ligga till grund för din personliga rådgivning."}
          </Text>
          {/* In the app this list is a screen of its own before this one. */}
          {!app && (
            <List spacing="xs" size="sm">
              <List.Item
                icon={
                  <Avatar>
                    <ChartPieIcon
                      height={"26px"}
                      color="var(--wallet-color-sambla-green-shade-700)"
                    />
                  </Avatar>
                }
              >
                Vi hämtar information om dina befintliga pensionsförsäkringar
              </List.Item>
              <List.Item
                icon={
                  <Avatar>
                    {" "}
                    <ChartBarIcon
                      height={"26px"}
                      color="var(--wallet-color-sambla-green-shade-700)"
                    />
                  </Avatar>
                }
              >
                Vi analyserar och tar fram personliga råd för att förbättra din
                pension
              </List.Item>
              <List.Item
                icon={
                  <Avatar>
                    <ArrowUpOnSquareStackIcon
                      height={"26px"}
                      color="var(--wallet-color-sambla-red-shade-600)"
                    />
                  </Avatar>
                }
              >
                Vi kommer INTE flytta eller ändra dina försäkringar.
              </List.Item>
            </List>
          )}
          <Title order={3} size="h5" my={"1rem"}>
            <FormattedMessage
              id="poaInfo.poaCard.sectionOne.title"
              defaultMessage="What is a power of attorney?"
            />
          </Title>
          <Text mb={"1rem"}>
            <FormattedMessage
              id="poaInfo.poaCard.sectionOne.paragraph"
              defaultMessage="A power of attorney gives a specifically designated person or company (the attorney-in-fact) the authority to perform actions on behalf of someone else (the principal)."
            />
          </Text>
          <Title order={3} size="h5" mb={"1rem"}>
            <FormattedMessage
              id="poaInfo.poaCard.sectionTwo.title"
              defaultMessage="What is a power of attorney and how do I revoke it?"
            />
          </Title>
          <Text mb={"1rem"}>
            <FormattedMessage
              id="poaInfo.poaCard.sectionTwo.paragraph"
              defaultMessage="A power of attorney for information is a limited power of attorney that grants the attorney-in-fact the right to receive specific information about insurance and pensions, for example from an insurance company. This power of attorney can be time-limited or valid indefinitely. You can revoke this power of attorney at any time. If the power of attorney is registered on fullmaktskollen.se, you can easily modify or revoke it there. For more information, visit www.fullmaktskollen.se."
            />
          </Text>
          {!app && (
            <>
              <Title order={3} size="h5" mb={"1rem"}>
                <FormattedMessage
                  id="poaInfo.poaCard.sectionThree.title"
                  defaultMessage="What is Fullmaktskollen?"
                />
              </Title>
              <Text mb={"1rem"}>
                <FormattedMessage
                  id="poaInfo.poaCard.sectionThree.paragraph"
                  defaultMessage="Fullmaktskollen.se is a service for managing powers of attorney in the life and pension sector. Fullmaktskollen.se makes it easy for individuals and companies to get an overview of their powers of attorney and how long they are valid. The service also allows you to sign and revoke powers of attorney."
                />
              </Text>
              <Flex justify={"center"}>
                <Image
                  src={FullmaktskollenImage}
                  alt="Fullmaktskollen"
                  h={38}
                  mt="md"
                  w="auto"
                  fit="contain"
                  data-testid="fullmaktskollen-image"
                />
              </Flex>
            </>
          )}
        </Paper>

        <FooterButtons app={app}>
          <Button
            onClick={() => {
              if (app) {
                trackEvent("pension_app_poa_back_clicked", {
                  step: APP_POA_STEP.poaInfo,
                });
              }
              (onBack ?? prevStep)();
            }}
            variant="default"
            data-testid="back-button"
          >
            <FormattedMessage id="bottomNav.back" defaultMessage="Tillbaka" />
          </Button>
          <Button
            onClick={() => {
              if (app) {
                trackEvent("pension_app_poa_next_clicked", {
                  step: APP_POA_STEP.poaInfo,
                });
              }
              nextStep();
            }}
            data-testid="next-button"
          >
            <FormattedMessage id="bottomNav.next" defaultMessage="Nästa" />
          </Button>
        </FooterButtons>
      </Flex>
    </Flex>
  );
}
